/**
 * Sendblue Tags Component
 * Displays and manages Sendblue tags for a contact
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  SendblueGroup,
  ContactTag,
  getAllTags,
  subscribeToContactTags,
  subscribeToAllTags,
  addContactToGroup,
  removeContactFromGroup,
  createSendblueGroup,
  saveTagToCache,
  getRandomTagColor,
} from '../../utils/sendblueTags';
import { useAuth } from '../../utils/AuthContext';
import './SendblueTags.css';

interface SendblueTagsProps {
  phoneNumber: string;
  leadId?: string;
  compact?: boolean;
}

export const SendblueTags: React.FC<SendblueTagsProps> = ({
  phoneNumber,
  leadId,
  compact = false,
}) => {
  const { user } = useAuth();
  const [contactTags, setContactTags] = useState<ContactTag[]>([]);
  const [allTags, setAllTags] = useState<SendblueGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Subscribe to contact tags
  useEffect(() => {
    if (!phoneNumber) return;

    const unsubscribe = subscribeToContactTags(
      phoneNumber,
      (tags) => setContactTags(tags),
      (error) => console.error('Error subscribing to contact tags:', error)
    );

    return () => unsubscribe();
  }, [phoneNumber]);

  // Subscribe to all available tags
  useEffect(() => {
    const unsubscribe = subscribeToAllTags(
      (tags) => setAllTags(tags),
      (error) => console.error('Error subscribing to all tags:', error)
    );

    // Also fetch initial tags
    getAllTags().then(setAllTags);

    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTag = async (tag: SendblueGroup) => {
    if (!phoneNumber || !user?.id) return;
    
    setIsLoading(true);
    try {
      const result = await addContactToGroup(tag.id, phoneNumber, user.id, tag.name);
      if (!result.success) {
        console.error('Failed to add tag:', result.error);
      }
    } catch (error) {
      console.error('Error adding tag:', error);
    } finally {
      setIsLoading(false);
      setShowDropdown(false);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!phoneNumber) return;
    
    setIsLoading(true);
    try {
      const result = await removeContactFromGroup(tagId, phoneNumber);
      if (!result.success) {
        console.error('Failed to remove tag:', result.error);
      }
    } catch (error) {
      console.error('Error removing tag:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim() || !user?.id) return;
    
    setIsCreating(true);
    try {
      const color = getRandomTagColor();
      const result = await createSendblueGroup(newTagName.trim(), color);
      
      if (result.success && result.group) {
        // Save to cache
        await saveTagToCache(result.group);
        // Add the contact to this new tag
        await addContactToGroup(result.group.id, phoneNumber, user.id, result.group.name);
        setNewTagName('');
      } else {
        console.error('Failed to create tag:', result.error);
      }
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setIsCreating(false);
      setShowDropdown(false);
    }
  };

  // Get tags that aren't already applied to this contact
  const availableTags = allTags.filter(
    (tag) => !contactTags.some((ct) => ct.tagId === tag.id)
  );

  if (!phoneNumber) {
    return null;
  }

  return (
    <div className={`sendblue-tags ${compact ? 'compact' : ''}`}>
      <div className="tags-container">
        {contactTags.length === 0 ? (
          <span className="no-tags">No tags</span>
        ) : (
          contactTags.map((tag) => {
            const tagInfo = allTags.find((t) => t.id === tag.tagId);
            return (
              <span
                key={tag.tagId}
                className="tag-chip"
                style={{ backgroundColor: tagInfo?.color || '#6B7280' }}
                title={`Added by ${tag.addedBy}`}
              >
                <span className="tag-name">{tag.tagName}</span>
                <button
                  className="tag-remove"
                  onClick={() => handleRemoveTag(tag.tagId)}
                  disabled={isLoading}
                  title="Remove tag"
                >
                  ×
                </button>
              </span>
            );
          })
        )}
        
        <div className="add-tag-container" ref={dropdownRef}>
          <button
            className="add-tag-btn"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={isLoading}
            title="Add tag"
          >
            +
          </button>
          
          {showDropdown && (
            <div className="tag-dropdown">
              <div className="dropdown-header">Add Tag</div>
              
              {availableTags.length > 0 ? (
                <div className="available-tags">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      className="tag-option"
                      onClick={() => handleAddTag(tag)}
                      disabled={isLoading}
                    >
                      <span
                        className="tag-color-dot"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="tag-option-name">{tag.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="no-available-tags">
                  All tags applied
                </div>
              )}
              
              <div className="create-tag-section">
                <div className="create-tag-divider">
                  <span>or create new</span>
                </div>
                <div className="create-tag-form">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="New tag name..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateTag();
                      }
                    }}
                    disabled={isCreating}
                  />
                  <button
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim() || isCreating}
                    title="Create tag"
                  >
                    {isCreating ? '...' : '✓'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendblueTags;
